// @flow
import SearchUrlFormatter from './formatters/SearchUrlFormatter';
import generateUrl from './utils/generateUrl';
import {
    QUERY,
    MIN_BATHROOMS,
    MIN_BEDROOMS,
    FILTER,
    PAGE
} from './models/SearchUrlParameters';

type Options = {
    siteContext?: Object
};

import type {SearchRequest, SearchRequestParam} from './models/SearchRequest';
const makeMinValueEnforcer = (searchRequest: SearchRequest) => (
    key: SearchRequestParam,
    minValue: number = 0
): SearchRequest => {
    return {
        ...(searchRequest[key] && parseInt(searchRequest[key], 10) > minValue
            ? {[key]: searchRequest[key]}
            : {})
    };
};
const pickCanonicalParams = (searchRequest: SearchRequest): SearchRequest => {
    const enforceMinValue = makeMinValueEnforcer(searchRequest);

    return {
        [QUERY]: searchRequest[QUERY] || '',
        [FILTER]: searchRequest[FILTER],
        ...enforceMinValue(MIN_BEDROOMS),
        ...enforceMinValue(MIN_BATHROOMS),
        ...enforceMinValue(PAGE, 1)
    };
};

/**
 * Generates a canonical /search URL for the given {@link SearchRequest} by
 *
 * 1. Lowercasing the destination keywords, replacing whitespace with dashes, and stripping out non-alphanumeric characters
 * 2. Removing everything from {@link SearchRequest} with the exception of `q`, `filter`, `minBedrooms`, `maxBedrooms`, `minBathrooms`, `maxBathrooms`, and `page`
 *
 * @example
 * toCanonicalURL({
 *  q: 'Austin, TX, USA',
 *  filter: ['filter:38'],
 *  petInclued: true
 * });
 * // returns
 * // '/search/keywords:austin-tx-usa/filter:38'
 *
 * toCanonicalURL({
 *  q: 'Austin, TX, USA',
 *  filter: ['filter:38'],
 *  petInclued: true
 * }, {
 *  siteContext: {
 *    hostname: 'www.vrbo.com',
 *    basePath: ''
 *  }
 * });
 * // returns
 * // 'https://www.vrbo.com/search/keywords:austin-tx-usa/filter:38'
 * @param searchRequest - Search Request model
 * @param {object} [options] - toURL options
 * @param {object} [options.siteContext] - global config siteContext data.
 * @param {string} [options.siteContext.hostname] - site hostname. If present, the URL will be generated as an absolute URL.
 * @param {string} [options.siteContext.basePath] - site base path
 * @returns {string} with the search page relative or absolute canonical URL for the given {@link SearchRequest} object.
 */
export default function toCanonicalURL(
    searchRequest: SearchRequest,
    options: Options = {}
) {
    const {siteContext} = options;

    const formatter = new SearchUrlFormatter();
    const canonicalSearchRequest = pickCanonicalParams(searchRequest);

    return generateUrl(
        siteContext && siteContext.hostname,
        formatter.format(canonicalSearchRequest, {canonicalizeKeywords: true}),
        siteContext && siteContext.basePath
    );
}
